import { useEffect, useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q5nlme3oq4jqumtgu0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const code = (parsed as Record<string, unknown> | null)?.code as string | undefined;
      const serverMsg = ((parsed as Record<string, unknown> | null)?.meta as Record<string, unknown> | null)?.message as string
        || (parsed as Record<string, unknown> | null)?.message as string
        || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '草榴技术讨论区',
    url: 'https://caoliu-tech.com/',
    description: '专注技术交流的核心社区板块，汇聚技术爱好者分享编程、网络、工具使用等实用经验。',
    email: 'contact@caoliu-tech.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@caoliu-tech.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '草榴技术讨论区是什么样的社区？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '草榴技术讨论区是一个专注技术交流的中文社区，我们汇聚了大量技术爱好者，涵盖编程开发、网络技术、工具软件、AI数据、系统运维和硬件数码等六大领域，致力于打造高质量的技术分享环境。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在草榴技术讨论区发布文章？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过联系我们的编辑团队提交技术文章。我们欢迎原创技术内容，包括教程、经验分享、工具推荐和技术观点等。所有投稿都会经过审核，确保内容质量。',
        },
      },
      {
        '@type': 'Question',
        name: '草榴技术讨论区的内容是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，草榴技术讨论区的所有技术文章和讨论内容完全免费开放。我们坚持知识共享的理念，致力于让每一位技术爱好者都能无障碍地获取优质技术内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系草榴技术讨论区的运营团队？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单、发送邮件至contact@caoliu-tech.com，或关注我们的社交媒体账号与我们取得联系。我们会在24小时内回复您的消息。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <SEOHead
        title="关于我们 - 草榴技术讨论区"
        description="了解草榴技术讨论区的品牌故事、社区口碑和联系方式。我们专注技术交流，汇聚编程开发、网络技术、工具软件等领域的技术爱好者。"
        keywords="草榴技术讨论区介绍,技术社区关于我们,草榴社区联系,技术论坛品牌"
        canonicalUrl="https://caoliu-tech.com/about"
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100/60 border-b border-background-200/60">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-6">
              关于我们
            </h1>
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              品牌介绍
            </h2>
            <div className="bg-background-100/70 rounded-xl p-6 md:p-8 border border-background-200/60">
              <p className="text-foreground-700 leading-relaxed mb-4">
                <strong className="text-foreground-900">草榴技术讨论区</strong>成立于2020年，是一个专注于技术交流与资源分享的中文社区平台。我们致力于打造一个高质量、互助友好的技术交流环境，让每一位技术爱好者都能在这里找到有价值的内容和志同道合的伙伴。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                我们的社区涵盖了<strong className="text-foreground-900">编程开发、网络技术、工具软件、AI与数据、系统与运维、硬件数码</strong>六大核心技术领域。无论你是初入技术领域的新手，还是经验丰富的资深开发者，都能在草榴技术讨论区找到属于自己的一片天地。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                草榴技术讨论区坚持<strong className="text-foreground-900">知识共享、开放交流</strong>的理念。我们相信技术的进步离不开社区的共同努力，每一次分享、每一次讨论，都可能激发新的灵感和突破。截止2026年，社区已累计发布超过<strong className="text-foreground-900">12,580篇</strong>优质技术文章，吸引了<strong className="text-foreground-900">68,420+</strong>名技术爱好者加入。
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100/60">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              社区口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "草榴技术讨论区是我每天必逛的技术社区，文章质量很高，讨论氛围也很好。在这里学到了很多实用的编程技巧。"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 text-xs font-bold">张</div>
                  <div>
                    <div className="text-xs font-medium text-foreground-800">张工</div>
                    <div className="text-xs text-foreground-400">全栈开发工程师</div>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "作为运维工程师，草榴的系统和运维板块帮了我大忙。Docker和Linux调优的文章写得特别详细，比很多付费课程都好。"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-200 flex items-center justify-center text-primary-700 text-xs font-bold">李</div>
                  <div>
                    <div className="text-xs font-medium text-foreground-800">李运维</div>
                    <div className="text-xs text-foreground-400">高级运维工程师</div>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "AI板块的文章质量特别高，从入门到进阶都有覆盖。ChatGPT API那篇教程让我成功搭建了公司的智能客服系统。"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent-200 flex items-center justify-center text-accent-700 text-xs font-bold">王</div>
                  <div>
                    <div className="text-xs font-medium text-foreground-800">王数据</div>
                    <div className="text-xs text-foreground-400">AI算法工程师</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              联系我们
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              <div className="bg-background-100/70 rounded-xl p-5 border border-background-200/60 text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">contact@caoliu-tech.com</p>
              </div>
              <div className="bg-background-100/70 rounded-xl p-5 border border-background-200/60 text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">办公地址</h3>
                <p className="text-xs text-foreground-500">中国·北京</p>
              </div>
              <div className="bg-background-100/70 rounded-xl p-5 border border-background-200/60 text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">响应时间</h3>
                <p className="text-xs text-foreground-500">工作日24小时内回复</p>
              </div>
            </div>

            <div className="bg-background-100/70 rounded-xl p-6 md:p-8 border border-background-200/60">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">发送消息</h3>
              <p className="text-sm text-foreground-500 mb-6">如有任何问题或建议，欢迎通过以下表单与我们联系</p>

              <form onSubmit={handleSubmit} data-readdy-form>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                    placeholder="请输入您的消息内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <div className="anti-spam-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {formStatus === 'success' && (
                  <div className="mb-4 px-4 py-3 rounded-lg bg-accent-100/80 text-accent-800 text-sm">
                    消息已发送成功！我们会尽快回复您。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送消息'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}